import React, { useMemo } from "react";
import "./CommonLearningPaths.scss";

/**
 * CommonLearningPaths
 * 
 * Shows the most common sequences of environments that students visit.
 * Helps teachers understand typical learning journeys through content.
 * 
 * Decision it enables: "What paths are students taking through my content?"
 */
export default function CommonLearningPaths({ 
  detailedSessions = [], 
  environmentData = {},
  limit = 6 
}) {
  
  const pathAnalysis = useMemo(() => {
    if (!detailedSessions || detailedSessions.length === 0) {
      return { paths: [], totalSessions: 0, avgPathLength: 0 };
    }

    // Extract environment sequences from each session
    const sequences = detailedSessions
      .map(session => {
        // Get unique environments in order of visit
        const envSequence = [];
        const seen = new Set();
        
        (session.events || []).forEach(event => {
          const envId = event.environmentId;
          if (envId && !seen.has(envId)) {
            seen.add(envId);
            envSequence.push(envId);
          }
        });
        
        return envSequence;
      })
      .filter(seq => seq.length >= 2); // Only include sessions with 2+ environments

    if (sequences.length === 0) {
      return { paths: [], totalSessions: detailedSessions.length, avgPathLength: 0 };
    }

    // Count path frequencies (using first 3-4 environments as the "path")
    const pathCounts = {};
    
    sequences.forEach(seq => {
      // Take first 4 environments as the path signature
      const pathKey = seq.slice(0, 4).join(" → ");
      const pathEnvs = seq.slice(0, 4);
      
      if (!pathCounts[pathKey]) {
        pathCounts[pathKey] = {
          key: pathKey,
          environments: pathEnvs,
          count: 0,
          fullPaths: [],
        };
      }
      pathCounts[pathKey].count++;
      pathCounts[pathKey].fullPaths.push(seq);
    });

    // Sort by frequency and take top N
    const sortedPaths = Object.values(pathCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, limit)
      .map((path, idx) => ({
        ...path,
        rank: idx + 1,
        percentage: Math.round((path.count / sequences.length) * 100),
        environmentNames: path.environments.map(envId => 
          environmentData[envId] || `Env ${envId.slice(0, 6)}...`
        ),
      }));

    // Calculate average path length
    const avgPathLength = sequences.reduce((sum, seq) => sum + seq.length, 0) / sequences.length;

    return {
      paths: sortedPaths,
      totalSessions: detailedSessions.length,
      sessionsWithPaths: sequences.length,
      avgPathLength: Math.round(avgPathLength * 10) / 10,
    };
  }, [detailedSessions, environmentData, limit]);

  if (pathAnalysis.paths.length === 0) {
    return (
      <div className="commonPaths">
        <div className="cpEmpty">
          <div className="cpEmptyIcon">🛤️</div>
          <div className="cpEmptyText">No learning paths detected yet</div>
          <div className="cpEmptySub">
            Paths appear when students visit multiple environments in a session
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="commonPaths">
      {/* Summary stats */}
      <div className="cpSummary">
        <div className="cpStat">
          <span className="cpStatValue">{pathAnalysis.sessionsWithPaths}</span>
          <span className="cpStatLabel">Multi-env sessions</span>
        </div>
        <div className="cpStat">
          <span className="cpStatValue">{pathAnalysis.avgPathLength}</span>
          <span className="cpStatLabel">Avg environments visited</span>
        </div>
        <div className="cpStat">
          <span className="cpStatValue">{pathAnalysis.paths.length}</span>
          <span className="cpStatLabel">Unique paths found</span>
        </div>
      </div>

      {/* Path list */}
      <div className="cpList">
        {pathAnalysis.paths.map((path) => (
          <div key={path.key} className="cpPath">
            {/* Rank badge */}
            <div className="cpRank">
              {path.rank === 1 && <span className="cpRankBadge cpRankBadge--gold">1</span>}
              {path.rank === 2 && <span className="cpRankBadge cpRankBadge--silver">2</span>}
              {path.rank === 3 && <span className="cpRankBadge cpRankBadge--bronze">3</span>}
              {path.rank > 3 && <span className="cpRankBadge">{path.rank}</span>}
            </div>

            {/* Path visualization */}
            <div className="cpPathContent">
              <div className="cpPathFlow">
                {path.environmentNames.map((envName, idx) => (
                  <React.Fragment key={idx}>
                    <span className="cpEnvBubble" title={envName}>
                      {envName.length > 20 ? envName.slice(0, 18) + '...' : envName}
                    </span>
                    {idx < path.environmentNames.length - 1 && (
                      <span className="cpArrow">→</span>
                    )}
                  </React.Fragment>
                ))}
                {path.environments.length < path.fullPaths[0]?.length && (
                  <span className="cpMore">+{path.fullPaths[0].length - path.environments.length} more</span>
                )}
              </div>
              
              <div className="cpPathMeta">
                <span className="cpPathCount">
                  <strong>{path.count}</strong> student{path.count !== 1 ? 's' : ''}
                </span>
                <span className="cpPathPercent">
                  ({path.percentage}% of journeys)
                </span>
              </div>
            </div>

            {/* Progress bar showing relative popularity */}
            <div className="cpPopularity">
              <div 
                className="cpPopularityBar"
                style={{ width: `${path.percentage}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer insight */}
      <div className="cpFooter">
        <div className="cpInsight">
          💡 The most common path accounts for <strong>{pathAnalysis.paths[0]?.percentage || 0}%</strong> of all learning journeys
        </div>
      </div>
    </div>
  );
}
